// src/components/Home/GlobalRankingsHome.jsx
import React, { useEffect, useState } from "react";
import {
  Card,
  Spinner,
  Alert,
  Tabs,
  Tab,
  Table,
} from "react-bootstrap";
import api from "../../api/axios";
import exploradorBadge from "../../assets/badges/explorer.png";
import cazadorBadge from "../../assets/badges/hunter.png";
import buscadorBadge from "../../assets/badges/pro.png";
import masterBadge from "../../assets/badges/master.png";
import leyendaBadge from "../../assets/badges/legend.png";
import activoTier from "../../assets/tiers/activo.png";
import destacadoTier from "../../assets/tiers/destacado.png";
import premiumTier from "../../assets/tiers/premium.png";
import eliteTier from "../../assets/tiers/elite.png";
import "./GlobalRankingsHome.css";



const BADGE_IMAGES = {
  EXPLORADOR_DESCUENTOS: exploradorBadge,
  CAZADOR_AHORRO: cazadorBadge,
  BUSCADOR_PROFESIONAL: buscadorBadge,
  MASTER_PROMOCIONES: masterBadge,
  LEYENDA_AHORRO: leyendaBadge,
};




// 🔹 Labels para badges de usuario
const USER_BADGE_LABELS = {
  EXPLORADOR_DESCUENTOS: "Explorador de descuentos",
  CAZADOR_AHORRO: "Cazador del ahorro",
  BUSCADOR_PROFESIONAL: "Buscador profesional",
  MASTER_PROMOCIONES: "Master en promociones",
  LEYENDA_AHORRO: "Leyenda del ahorro",
};

// helper para obtener el código de nivel “real” según código o puntos
const getUserBadgeCode = (user) => {
  const pts = user.points_total || 0;
  const fromPoints = computeUserBadgeFromPoints(pts);
  return (user.badge_code || fromPoints || "").toUpperCase();
};


function computeUserBadgeFromPoints(totalPoints) {
  if (totalPoints >= 500) return "LEYENDA_AHORRO";
  if (totalPoints >= 299) return "MASTER_PROMOCIONES";
  if (totalPoints >= 149) return "BUSCADOR_PROFESIONAL";
  if (totalPoints >= 49) return "CAZADOR_AHORRO";
  return "EXPLORADOR_DESCUENTOS";
}

function getUserBadgeLabel(badgeCode, pointsTotal) {
  const code = (badgeCode || computeUserBadgeFromPoints(pointsTotal))?.toUpperCase();
  return USER_BADGE_LABELS[code] || "Explorador de descuentos";
}

// 🔹 Tiers negocio
const TIER_LABELS = {
  ACTIVO: "Activo",
  DESTACADO: "Destacado",
  PREMIUM: "Premium",
  ELITE: "Elite",
};

const TIER_IMAGES = {
  ACTIVO: activoTier,
  DESTACADO: destacadoTier,
  PREMIUM: premiumTier,
  ELITE: eliteTier,
};



// Helper para clase de fila según posición
function getRowClassByIndex(index) {
  if (index === 0) return "table-warning";   // 1º
  if (index === 1) return "table-secondary"; // 2º
  if (index === 2) return "table-light";     // 3º
  return "";
}

// 🔹 TOP CLIENTES GLOBAL (tabla mejorada + tu posición)
const TopClientes = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState("ALL"); // 👈 filtro por nivel

  // Info de usuario logueado (si la hay)
  let currentUser = null;
  try {
    const raw = localStorage.getItem("user");
    if (raw) currentUser = JSON.parse(raw);
  } catch (e) {
    currentUser = null;
  }
  const currentUserId = currentUser?.id || currentUser?.user_id || null;

  useEffect(() => {
    const token = localStorage.getItem("token");

    const fetchRanking = async () => {
      try {
        const headers = token
          ? { Authorization: `Bearer ${token}` }
          : undefined;

        const res = await api.get("/points/ranking/users", { headers });
        const data = res.data;
        setItems(data.items || []); // listado completo
      } catch (err) {
        console.error("Error cargando ranking global de clientes:", err);
        setError("No pudimos cargar el ranking de clientes.");
      } finally {
        setLoading(false);
      }
    };

    fetchRanking();
  }, []);

  if (loading) {
    return (
      <div className="text-center my-3">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="warning" className="mb-0">
        {error}
      </Alert>
    );
  }

  // 🔹 Solo rankeamos usuarios con puntos > 0
  const itemsWithPoints = items.filter((u) => (u.points_total || 0) > 0);

  // Nadie tiene puntos todavía
  if (itemsWithPoints.length === 0) {
    return (
      <>
        <p className="text-muted mb-0">
          Todavía no hay clientes con puntos acumulados.
        </p>

        {currentUserId && (
          <div className="mt-2 p-2 rounded bg-light border">
            <div style={{ fontSize: ".9rem" }}>
              <strong>Tu posición actual:</strong>{" "}
              <span>Aún no sumaste ningún punto.</span>
            </div>
            <div className="text-muted" style={{ fontSize: ".8rem" }}>
              Reclamá tu primer beneficio para empezar a aparecer en el
              ranking 🎯
            </div>
          </div>
        )}
      </>
    );
  }

  // 🔹 Aplico filtro por nivel (sobre los que tienen puntos)
  const rankedFiltered =
    selectedLevel === "ALL"
      ? itemsWithPoints
      : itemsWithPoints.filter((u) => getUserBadgeCode(u) === selectedLevel);

  const topFive = rankedFiltered.slice(0, 5);

  // 🔹 Usuario en el listado completo (aunque tenga 0)
  const myFromAll = currentUserId
    ? items.find((u) => u.id === currentUserId)
    : null;
  const myPoints = myFromAll?.points_total ?? 0;

  // 🔹 Posición del usuario dentro de los que tienen puntos (>0, ranking global)
  const myIndexRanked = currentUserId
    ? itemsWithPoints.findIndex((u) => u.id === currentUserId)
    : -1;
  const myRank = myIndexRanked >= 0 ? myIndexRanked + 1 : null;

  // 🔹 Progreso textual (solo si tiene puntos)
  let progressText = null;
  if (myRank && myPoints > 0) {
    let targetRank = null;

    if (itemsWithPoints.length >= 10 && myRank > 10) {
      targetRank = 10;
    } else if (itemsWithPoints.length >= 5 && myRank > 5) {
      targetRank = 5;
    }

    if (targetRank && itemsWithPoints[targetRank - 1]) {
      const targetPoints = itemsWithPoints[targetRank - 1].points_total;
      const delta = targetPoints - myPoints;
      if (delta > 0) {
        progressText = `Te faltan ${delta} pts para alcanzar el Top ${targetRank}.`;
      }
    }

    if (!progressText) {
      if (myRank <= 5) {
        progressText = "¡Estás en el Top 5! Seguí sumando para mantener tu lugar 👏";
      } else if (myRank <= 10) {
        progressText =
          "¡Ya estás dentro del Top 10! Un poco más y subís de posición 🚀";
      }
    }
  }

  return (
    <>
      {/* Filtros por nivel con iconos */}
<div className="mb-3 d-flex flex-wrap gap-2 align-items-center">
  
  {/* BOTÓN "TODOS" */}
  <button
    className={
      "btn btn-sm " +
      (selectedLevel === "ALL" ? "btn-dark" : "btn-outline-dark")
    }
    onClick={() => setSelectedLevel("ALL")}
  >
    Todos
  </button>

  {/* ICONOS DE BADGES */}
  {Object.keys(BADGE_IMAGES).map((code) => {
    const isActive = selectedLevel === code;
    return (
      <div
        key={code}
        onClick={() => setSelectedLevel(code)}
        style={{
          cursor: "pointer",
          padding: 4,
          borderRadius: 8,
          border: isActive ? "2px solid #000" : "1px solid #ccc",
          background: isActive ? "#00000010" : "#fff",
        }}
      >
        <img
          src={BADGE_IMAGES[code]}
          alt={code}
          style={{ width: 40, height: 40 }}
        />
      </div>
    );
  })}
</div>


      {/* Tabla TOP 5 filtrada por nivel */}
      {topFive.length === 0 ? (
        <p className="text-muted mb-0">
          No hay clientes en este nivel todavía.
        </p>
      ) : (
        <Table responsive size="sm" hover className="mb-0 align-middle ranking-table">
          <thead>
            <tr>
              <th style={{ width: 60 }}>#</th>
              <th>Cliente</th>
              <th>Nivel</th>
              <th className="text-end">Puntos</th>
            </tr>
          </thead>
          <tbody>
            {topFive.map((user, index) => {
              const rowClassBase = getRowClassByIndex(index);
              const isMe = currentUserId && user.id === currentUserId;
              const badgeLabel = getUserBadgeLabel(
                user.badge_code,
                user.points_total
              );

              const rowClass = isMe
                ? `${rowClassBase} table-primary`
                : rowClassBase;

              return (
                <tr key={user.id} className={rowClass}>
                  <td>
                    <strong>{index + 1}</strong>
                  </td>
                  <td>
                    <div>{user.name || "Sin nombre"}</div>
                    {user.email && (
                      <div
                        className="text-muted"
                        style={{ fontSize: ".8rem" }}
                      >
                        {user.email}
                      </div>
                    )}
                  </td>
              <td>
  <div className="d-flex align-items-center gap-2">
    <img
      src={BADGE_IMAGES[getUserBadgeCode(user)]}
      alt={badgeLabel}
      style={{ width: 28, height: 28 }}
    />
    <span style={{ fontSize: ".85rem" }}>{badgeLabel}</span>
  </div>
</td>

                  <td className="text-end">
                    <strong>{user.points_total}</strong> pts
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      {/* Bloque de "Tu posición" (si hay usuario logueado) */}
      {currentUserId && (
        <div className="mt-2 p-2 rounded bg-light border">
          {myPoints > 0 && myRank ? (
            <>
              <div style={{ fontSize: ".9rem" }}>
                <strong>Tu posición actual:</strong>{" "}
                <span>
                  #{myRank} · {myPoints} pts
                </span>
              </div>
              {progressText && (
                <div className="text-muted" style={{ fontSize: ".8rem" }}>
                  {progressText}
                </div>
              )}
            </>
          ) : (
            <>
              <div style={{ fontSize: ".9rem" }}>
                <strong>Tu posición actual:</strong>{" "}
                <span>Aún no sumaste ningún punto.</span>
              </div>
              <div className="text-muted" style={{ fontSize: ".8rem" }}>
                Reclamá tu primer beneficio para empezar a aparecer en el
                ranking 🎯
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};



// 🔹 TOP NEGOCIOS GLOBAL (tabla mejorada + top 5)
const TopNegocios = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");

    const fetchRanking = async () => {
      try {
        const headers = token
          ? { Authorization: `Bearer ${token}` }
          : undefined;

        const res = await api.get("/points/ranking/businesses", { headers });
        const data = res.data;
        setItems(data.items || []);
      } catch (err) {
        console.error("Error cargando ranking global de negocios:", err);
        setError("No pudimos cargar el ranking de negocios.");
      } finally {
        setLoading(false);
      }
    };

    fetchRanking();
  }, []);

  if (loading) {
    return (
      <div className="text-center my-3">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="warning" className="mb-0">
        {error}
      </Alert>
    );
  }

  if (items.length === 0) {
    return (
      <p className="text-muted mb-0">
        Todavía no hay negocios con puntos acumulados.
      </p>
    );
  }

  const topFive = items.slice(0, 5);

  return (
    <Table responsive size="sm" hover className="mb-0 align-middle ranking-table">
      <thead>
        <tr>
          <th style={{ width: 60 }}>#</th>
          <th>Negocio</th>
          <th>Tier</th>
          <th className="text-end">Puntos</th>
        </tr>
      </thead>
      <tbody>
        {topFive.map((biz, index) => {
          const rowClass = getRowClassByIndex(index);
          const tierLabel = TIER_LABELS[biz.tier_code] || biz.tier_code;
          return (
            <tr key={biz.id} className={rowClass}>
              <td>
                <strong>{index + 1}</strong>
              </td>
              <td>
                <div>
                  {biz.slug ? (
                    <a
                      href={`/catalogo/${biz.slug}`}
                      style={{ textDecoration: "none" }}
                    >
                      {biz.name}
                    </a>
                  ) : (
                    biz.name
                  )}
                </div>
                {biz.slug && (
                  <div className="text-muted" style={{ fontSize: ".8rem" }}>
                    @{biz.slug}
                  </div>
                )}
              </td>
<td>
  <div className="d-flex align-items-center gap-2">
    {TIER_IMAGES[biz.tier_code] && (
      <img
        src={TIER_IMAGES[biz.tier_code]}
        alt={tierLabel}
        style={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          objectFit: "cover",
          boxShadow: "0 0 4px rgba(0,0,0,0.15)",
        }}
      />
    )}
    <span>{tierLabel}</span>
  </div>
</td>


              <td className="text-end">
                <strong>{biz.points_total}</strong> pts
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};

const GlobalRankingsHome = () => {
  return (
    <Card className="mt-3 shadow-sm border-0 ranking-card">
      <Card.Body>
        <div className="ranking-header mb-3">
          <h4 className="mb-1">Ranking global</h4>
          <p className="mb-0 ranking-subtitle">
            Comparativa de puntos entre clientes y comercios.
          </p>
        </div>

        <Tabs defaultActiveKey="clients" className="mb-0 ranking-tabs">
          <Tab eventKey="clients" title="Top clientes">
            <div className="mt-3">
              <TopClientes />
            </div>
          </Tab>
          <Tab eventKey="businesses" title="Top negocios">
            <div className="mt-3">
              <TopNegocios />
            </div>
          </Tab>
        </Tabs>
      </Card.Body>
    </Card>
  );
};

export default GlobalRankingsHome;
